import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { adminWaitlistInsights, adminWaitlistFlagged, adminWaitlistFlaggedPurge, adminRecycleBin, adminRecycleBinRestore, adminWhitelistGet, adminWhitelistSet, adminApprovalsList, adminApprovalsApprove } from "@/lib/api";

// Distinctive palette for the source donut — matches brand accent colors
const SOURCE_COLORS = ["#a855f7", "#60a5fa", "#22c55e", "#facc15", "#f472b6", "#00F0FF", "#fb923c", "#e2e8f0"];

/**
 * Combined admin card:
 *   - Waitlist Insights (source breakdown, organic vs referral, fraud + active-referrer counts)
 *   - Flagged Signups list (anti-fraud guard)
 *   - Pending Approvals queue (multi-owner approval workflow)
 */
export const InsightsAndSafetyCard = ({ isOwner = false }) => {
  const [insights, setInsights] = useState(null);
  const [flagged, setFlagged] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [purging, setPurging] = useState(false);
  const [bin, setBin] = useState([]);
  const [whitelist, setWhitelist] = useState("");
  const [savingWl, setSavingWl] = useState(false);

  const refresh = async () => {
    try {
      const promises = [
        adminWaitlistInsights(),
        adminWaitlistFlagged(),
        adminApprovalsList("pending"),
      ];
      if (isOwner) {
        promises.push(adminRecycleBin(), adminWhitelistGet());
      }
      const results = await Promise.all(promises);
      setInsights(results[0]);
      setFlagged(results[1].items || []);
      setApprovals(results[2].items || []);
      if (isOwner) {
        setBin(results[3].items || []);
        setWhitelist((results[4].domains || []).join(", "));
      }
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);

  const restore = async (email) => {
    try {
      await adminRecycleBinRestore(email);
      toast.success(`Restored ${email}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  const saveWhitelist = async () => {
    setSavingWl(true);
    try {
      const domains = whitelist.split(/[,\s]+/).map((d) => d.trim().toLowerCase()).filter(Boolean);
      const r = await adminWhitelistSet(domains);
      toast.success(`Whitelist saved (${r.domains.length} domains)`);
      setWhitelist(r.domains.join(", "));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingWl(false); }
  };

  const approve = async (ap_id) => {
    try {
      await adminApprovalsApprove(ap_id);
      toast.success("Approval granted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    }
  };

  const purgeFlagged = async () => {
    if (flagged.length === 0) return;
    if (!window.confirm(`Permanently delete all ${flagged.length} flagged signups? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const r = await adminWaitlistFlaggedPurge();
      toast.success(`Purged ${r.purged} flagged signup${r.purged === 1 ? "" : "s"}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Purge failed");
    } finally { setPurging(false); }
  };

  if (!insights) {
    return <div className="text-zinc-300 font-mono text-xs">loading insights…</div>;
  }

  return (
    <section data-testid="insights-and-safety-card" className="space-y-6">
      {/* Waitlist Insights */}
      <div className="border border-zinc-800 bg-zinc-950/60 p-5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] font-mono mb-3">// waitlist insights</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div><div className="font-heading text-3xl font-black neon-green" data-testid="ins-total">{insights.total}</div><div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono mt-1">Total signups</div></div>
          <div><div className="font-heading text-3xl font-black text-[#60a5fa]" data-testid="ins-organic">{insights.organic}</div><div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono mt-1">Organic</div></div>
          <div><div className="font-heading text-3xl font-black text-[#a855f7]" data-testid="ins-referral">{insights.via_referral}</div><div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono mt-1">Via referral</div></div>
          <div><div className="font-heading text-3xl font-black text-[#facc15]" data-testid="ins-referrers">{insights.active_referrers}</div><div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-mono mt-1">Active referrers</div></div>
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono mb-2">// by source</div>
        {insights.sources.length === 0 ? (
          <div className="text-xs text-zinc-300 font-mono">no data yet</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-64" data-testid="insights-donut">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={insights.sources.map((s) => ({ name: s.source, value: s.signups }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={2}
                    stroke="#0b0b0b"
                    strokeWidth={2}
                  >
                    {insights.sources.map((_s, i) => (
                      <Cell key={`c-${i}`} fill={SOURCE_COLORS[i % SOURCE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "#000", border: "1px solid #334155", fontFamily: "monospace", fontSize: 11 }}
                    formatter={(v, n) => [`${v} signups`, n]}
                  />
                  <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 10, color: "#e2e8f0" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="border border-zinc-800">
              {insights.sources.map((s, i) => (
                <div key={s.source} data-testid={`ins-source-${s.source}`} className="flex items-center justify-between px-4 py-2 border-b border-zinc-900 last:border-b-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block w-3 h-3 rounded-full shrink-0"
                      style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }}
                    />
                    <div className="font-mono text-sm text-white truncate">{s.source}</div>
                  </div>
                  <div className="flex items-center gap-4 text-xs font-mono shrink-0">
                    <div className="text-zinc-200">{s.signups}</div>
                    <div className="text-[#a855f7]">{s.referred_signups} ref</div>
                    <div className="text-zinc-400 w-12 text-right">{s.share_pct}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Fraud Guard */}
      <div className="border border-zinc-800 bg-zinc-950/60 p-5">
        <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#ef4444] font-mono">// anti-fraud queue</div>
          <div className="flex items-center gap-3">
            <div className="text-xs text-zinc-300 font-mono" data-testid="fraud-count">{flagged.length} flagged</div>
            {isOwner && flagged.length > 0 && (
              <button
                data-testid="fraud-purge-btn"
                onClick={purgeFlagged}
                disabled={purging}
                className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#ef4444] hover:bg-[#f87171] px-3 py-2 font-black disabled:opacity-50 transition-colors"
              >
                {purging ? "Purging…" : `Purge all ${flagged.length} →`}
              </button>
            )}
          </div>
        </div>
        {flagged.length === 0 ? (
          <div className="text-xs text-zinc-300 font-mono">// nothing suspicious — leaderboard is clean 🛡️</div>
        ) : (
          <div className="border border-zinc-800 max-h-56 overflow-y-auto">
            {flagged.map((r) => (
              <div key={r.email} data-testid={`fraud-row-${r.email}`} className="px-4 py-2 border-b border-zinc-900 last:border-b-0">
                <div className="font-mono text-sm text-white">{r.email}</div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#ef4444] font-mono mt-1">
                  {(r.fraud_reasons || []).join(" · ")}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Multi-owner approvals */}
      <div className="border border-zinc-800 bg-zinc-950/60 p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#facc15] font-mono">// pending approvals · 2-owner rule</div>
          <div className="text-xs text-zinc-300 font-mono" data-testid="approvals-count">{approvals.length} pending</div>
        </div>
        <div className="text-xs text-zinc-300 mb-3">Big money-moving actions (payouts, refunds, role changes) require a SECOND admin/owner to click Approve before they execute. Ready for when payments unlock.</div>
        {approvals.length === 0 ? (
          <div className="text-xs text-zinc-300 font-mono">// nothing pending 🔒</div>
        ) : (
          <div className="border border-zinc-800">
            {approvals.map((a) => (
              <div key={a.approval_id} data-testid={`approval-row-${a.approval_id}`} className="flex items-center justify-between px-4 py-3 border-b border-zinc-900 last:border-b-0">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-white truncate">{a.action}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-mono mt-1">target · {a.target_uid} · requested by {a.requester_uid?.slice(0, 12)}</div>
                </div>
                <button
                  data-testid={`approve-${a.approval_id}`}
                  onClick={() => approve(a.approval_id)}
                  className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#facc15] hover:bg-[#fde047] px-3 py-2 font-black transition-colors"
                >
                  Approve
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Domain whitelist + Recycle Bin — owner only */}
      {isOwner && (
        <>
          <div className="border border-zinc-800 bg-zinc-950/60 p-5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#22c55e] font-mono mb-3">// domain whitelist · overrides disposable check</div>
            <div className="text-xs text-zinc-300 mb-3">Comma-separated domains that should never be auto-flagged.</div>
            <textarea
              data-testid="whitelist-input"
              value={whitelist}
              onChange={(e) => setWhitelist(e.target.value)}
              rows={2}
              placeholder="gmail.com, outlook.com"
              className="w-full bg-black border border-zinc-700 focus:border-[#22c55e] outline-none px-3 py-2 text-white text-xs font-mono resize-y"
            />
            <button
              data-testid="whitelist-save-btn"
              onClick={saveWhitelist}
              disabled={savingWl}
              className="mt-3 text-[10px] tracking-[0.3em] uppercase text-black bg-[#22c55e] hover:bg-[#4ade80] px-4 py-2 font-black disabled:opacity-50 transition-colors"
            >
              {savingWl ? "Saving…" : "Save whitelist"}
            </button>
          </div>

          <div className="border border-zinc-800 bg-zinc-950/60 p-5">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#facc15] font-mono">// recycle bin · 24h restore window</div>
              <div className="text-xs text-zinc-300 font-mono" data-testid="bin-count">{bin.length} in bin</div>
            </div>
            {bin.length === 0 ? (
              <div className="text-xs text-zinc-300 font-mono">// nothing to restore ♻️</div>
            ) : (
              <div className="border border-zinc-800 max-h-56 overflow-y-auto">
                {bin.map((r) => (
                  <div key={r.email} data-testid={`bin-row-${r.email}`} className="flex items-center justify-between px-4 py-2 border-b border-zinc-900 last:border-b-0">
                    <div className="min-w-0">
                      <div className="font-mono text-sm text-white truncate">{r.email}</div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-mono mt-1">deleted {(r.deleted_at || "").slice(0, 16)}</div>
                    </div>
                    <button
                      data-testid={`bin-restore-${r.email}`}
                      onClick={() => restore(r.email)}
                      className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#facc15] hover:bg-[#fde047] px-3 py-2 font-black transition-colors"
                    >
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

